import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useRef, useState } from 'react'

import { playgroundsApi } from '../../data/api/playgrounds.api'
import { queueApi } from '../../data/api/queue.api'
import {
  isPlaygroundPending,
  type PlaygroundGeneration,
  type PlaygroundGenerationRequest,
} from '../models/PlaygroundGeneration'

import { workspaceMediaItemKeys } from './useWorkspaceMediaItems'

export const playgroundKeys = {
  all: ['playgrounds'] as const,
  workspace: (workspaceId: string) => ['playgrounds', workspaceId] as const,
  images: (workspaceId: string) => ['playgrounds', workspaceId, 'images'] as const,
  active: (workspaceId: string) => ['playgrounds', workspaceId, 'active'] as const,
  status: (workspaceId: string, activityId: string) =>
    ['playgrounds', workspaceId, 'status', activityId] as const,
  generate: (workspaceId: string) => ['playgrounds', workspaceId, 'generate'] as const,
}

export function usePlaygroundImages(workspaceId: string, enabled: boolean) {
  const queryClient = useQueryClient()
  const completed = useRef(new Set<string>())
  const [tracked, setTracked] = useState<Record<string, PlaygroundGeneration>>({})
  const [cancellingIds, setCancellingIds] = useState<Set<string>>(() => new Set())
  const [lastResult, setLastResult] = useState<PlaygroundGeneration | null>(null)
  const images = useQuery({
    queryKey: playgroundKeys.images(workspaceId),
    queryFn: () => playgroundsApi.images(workspaceId).then((response) => response.data),
    enabled,
  })
  const active = useQuery({
    queryKey: playgroundKeys.active(workspaceId),
    queryFn: () => playgroundsApi.active(workspaceId).then((response) => response.data),
    enabled,
    refetchOnMount: 'always',
  })

  const submission = useMutation({
    mutationKey: playgroundKeys.generate(workspaceId),
    mutationFn: (request: PlaygroundGenerationRequest) =>
      playgroundsApi.generate(workspaceId, request).then((response) => response.data),
    onMutate: () => {
      setLastResult(null)
    },
    onSuccess: (data) => {
      if (completed.current.has(data.activityId)) return
      setTracked((previous) => ({
        ...previous,
        [data.activityId]: {
          activityId: data.activityId,
          workspaceId: data.workspaceId,
          numberOfImages: data.numberOfImages,
          status: data.status,
          generatedCount: 0,
          resultFileIds: [],
          error: null,
          canCancel: true,
        },
      }))
      void queryClient.invalidateQueries({
        queryKey: playgroundKeys.status(workspaceId, data.activityId),
      })
    },
    onError: (error) => {
      console.error('Playground generation request failed', error)
      void queryClient.invalidateQueries({ queryKey: playgroundKeys.active(workspaceId) })
    },
  })

  const cancellation = useMutation({
    mutationFn: (activityId: string) =>
      queueApi.cancel(activityId).then((response) => response.data),
    onMutate: (activityId) => {
      setCancellingIds((previous) => new Set(previous).add(activityId))
    },
    onSuccess: (_data, activityId) => {
      void queryClient.invalidateQueries({
        queryKey: playgroundKeys.status(workspaceId, activityId),
      })
      void queryClient.invalidateQueries({ queryKey: playgroundKeys.active(workspaceId) })
      void queryClient.invalidateQueries({ queryKey: ['queue'] })
    },
    onSettled: (_data, _error, activityId) => {
      setCancellingIds((previous) => {
        const next = new Set(previous)
        next.delete(activityId)
        return next
      })
    },
  })

  useEffect(() => {
    if (!active.data) return
    setTracked((previous) => {
      const next = { ...previous }
      for (const activity of active.data) {
        if (isPlaygroundPending(activity.status) && !completed.current.has(activity.activityId)) {
          next[activity.activityId] = activity
        }
      }
      return next
    })
  }, [active.data])

  const statuses = useQueries({
    queries: Object.keys(tracked).map((activityId) => ({
      queryKey: playgroundKeys.status(workspaceId, activityId),
      queryFn: () =>
        playgroundsApi.status(workspaceId, activityId).then((response) => response.data),
      enabled,
      refetchInterval: (query: { state: { data: PlaygroundGeneration | undefined } }) =>
        query.state.data && !isPlaygroundPending(query.state.data.status) ? false : 5000,
    })),
  })

  const activities = Object.values(tracked).flatMap((fallback, index) => {
    const activity = statuses[index]?.data ?? fallback
    return isPlaygroundPending(activity.status) ? [activity] : []
  })

  useEffect(() => {
    for (const query of statuses) {
      const activity = query.data
      if (
        !activity ||
        isPlaygroundPending(activity.status) ||
        completed.current.has(activity.activityId)
      )
        continue
      completed.current.add(activity.activityId)
      setLastResult(activity)
      setTracked((previous) =>
        Object.fromEntries(Object.entries(previous).filter(([id]) => id !== activity.activityId)),
      )
      void queryClient.invalidateQueries({ queryKey: playgroundKeys.images(workspaceId) })
      void queryClient.invalidateQueries({ queryKey: playgroundKeys.active(workspaceId) })
      void queryClient.invalidateQueries({
        queryKey: workspaceMediaItemKeys.workspace(workspaceId),
      })
      void queryClient.invalidateQueries({ queryKey: ['queue'] })
    }
  }, [statuses, queryClient, workspaceId])

  const generate = async (request: PlaygroundGenerationRequest) => {
    if (
      queryClient.isMutating({ mutationKey: playgroundKeys.generate(workspaceId) }) > 0 ||
      Object.keys(tracked).length > 0
    )
      return
    await submission.mutateAsync(request).catch(() => undefined)
  }

  return {
    images,
    active,
    activities,
    cancellation,
    cancellingIds,
    isSubmitting: submission.isPending,
    generate,
    submitError: submission.isError
      ? 'Generation could not be queued. Check the settings and try again. If the request timed out, refresh the activity status before submitting again.'
      : null,
    lastResult,
    pendingCount: Math.max(
      submission.isPending ? submission.variables.numberOfImages : 0,
      Object.values(tracked).reduce((sum, activity) => sum + activity.numberOfImages, 0),
    ),
    statusError: statuses.some((query) => query.isError),
    retryStatuses: () => {
      void active.refetch()
      for (const query of statuses) {
        void query.refetch()
      }
    },
  }
}
